import { Stack, Text, Textarea, UnstyledButton } from "@mantine/core";
import { Clock3, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { Choice } from "../../../ui/Choice";
import type { Composer } from "../composer/use-composer";
import { fitShots, shotBudget, type ShotDraft } from "./shots";
import classes from "./shots.module.css";

/** Live's shot placeholders, a tease, a reveal and a payoff, cycling from the fourth shot. */
const PLACEHOLDERS = [
  "Extreme close-up of the product's texture and details, macro lens, dramatic lighting.",
  "The full product is revealed on a clean pedestal as the camera pulls back, studio lighting.",
  "A person happily uses the product in a bright, real-life setting.",
] as const;

/**
 * Live's Multi-Shot editor (#230, captures 17 to 17d, L03): the "Shots" pill with a round button
 * per shot and a "+", the shot's own prompt and counter, and its length, "1s" with a clock. The
 * active shot, once there are two, is removed by its trash icon. "+" adds a shot of the shortest
 * length while fewer than the most shots and the budget left holds one (live's
 * `useShotsBudget`); a shot's lengths run up to what the others leave. Ours keeps the shots at
 * the model's shortest video in all (spec web/shot-defaults): a shot's lengths start where the
 * others leave enough, and a removed shot's length goes to the last one left where needed.
 */
export function ShotsEditor({ c }: { c: Composer }) {
  const [active, setActive] = useState(0);
  const caps = c.multiShot;
  if (caps === null) return null;
  const shots = c.draft.shots;
  const at = Math.min(active, shots.length - 1);
  const shot = shots[at];
  if (shot === undefined) return null;
  const budget = shotBudget(shots, caps, at, c.shortestVideo);
  const setShots = (next: ShotDraft[]) => c.set({ shots: next });
  const change = (patch: Partial<ShotDraft>) =>
    setShots(shots.map((each, index) => (index === at ? { ...each, ...patch } : each)));
  const add = () => {
    setShots([...shots, { prompt: "", durationSeconds: budget.min }]);
    setActive(shots.length);
  };
  const remove = () => {
    setShots(fitShots(shots.filter((_, index) => index !== at), caps, c.shortestVideo));
    setActive(Math.max(Math.min(at, shots.length - 2), 0));
  };
  const lengths = Array.from({ length: budget.activeMax - budget.activeMin + 1 }, (_, step) => {
    const seconds = budget.activeMin + step;
    return { value: String(seconds), label: `${seconds}s` };
  });
  return (
    <Stack gap={12}>
      <div className={classes.navigator}>
        <span className={classes.label}>Shots</span>
        <span className={classes.items}>
          {shots.map((_, index) => {
            const isActive = index === at;
            const removable = isActive && shots.length > 1;
            return (
              <UnstyledButton
                // Shots have no identity of their own: their place is what tells them apart.
                key={index}
                className={classes.shot}
                data-active={isActive || undefined}
                data-removable={removable || undefined}
                aria-label={removable ? `Remove shot ${index + 1}` : `Shot ${index + 1}`}
                aria-current={isActive || undefined}
                onClick={removable ? remove : () => setActive(index)}
              >
                <span className={classes.number} aria-hidden>
                  {index + 1}
                </span>
                {removable ? (
                  <span className={classes.trash} aria-hidden>
                    <Trash2 size={14} strokeWidth={2} />
                  </span>
                ) : null}
              </UnstyledButton>
            );
          })}
          <UnstyledButton
            className={classes.add}
            aria-label="Add a shot"
            disabled={!budget.canAdd}
            onClick={add}
          >
            <Plus size={12} strokeWidth={2} aria-hidden />
          </UnstyledButton>
        </span>
      </div>
      <Stack gap={4}>
        <Textarea
          variant="unstyled"
          aria-label={`Shot ${at + 1} prompt`}
          placeholder={PLACEHOLDERS[at % PLACEHOLDERS.length]}
          autosize
          minRows={1}
          maxRows={5}
          maxLength={c.promptMax}
          value={shot.prompt}
          onChange={(event) => change({ prompt: event.currentTarget.value })}
        />
        <Text size="xs" c="var(--cai-color-text-muted)" ta="right">
          {shot.prompt.length} / {c.promptMax}
        </Text>
      </Stack>
      <div>
        <Choice
          setting={`Shot ${at + 1} duration`}
          icon={<Clock3 size={16} strokeWidth={1.5} />}
          chevron={false}
          width={80}
          value={String(shot.durationSeconds)}
          options={lengths}
          onChange={(next) => change({ durationSeconds: Number(next) })}
        />
      </div>
    </Stack>
  );
}
